/**
 * FileUploadManagerCart - Simple file input display manager
 * 
 * Just shows selected files in a list, lets the native HTML5 file input handle everything else
 */

export default class FileUploadManagerCart {
    private fileInput: HTMLInputElement | null;
    private uploadList: HTMLElement | null;
    private allFiles: File[] = []; // Keep track of all selected files

    constructor() {
        this.fileInput = document.getElementById('file-upload') as HTMLInputElement;
        this.uploadList = document.querySelector('.base-cart-form__upload-list') as HTMLElement;

        if (this.fileInput && this.uploadList) {
            this.initEvents();
            this.clearExampleFiles();
        }
    }

    private initEvents(): void {
        if (!this.fileInput) return;

        // Listen to file input changes and add new files to existing ones
        this.fileInput.addEventListener('change', (event) => {
            this.handleFileSelection(event);
        });
    }

    private clearExampleFiles(): void {
        if (!this.uploadList) return;
        
        // Clear any existing items
        this.uploadList.innerHTML = '';
    }

    private handleFileSelection(event: Event): void {
        const target = event.target as HTMLInputElement;
        if (!target.files || target.files.length === 0) return;

        // Get newly selected files
        const newFiles = Array.from(target.files);
        
        // Add new files to existing files
        newFiles.forEach(newFile => {
            // Check if file already exists (by name and size)
            const exists = this.allFiles.some(existingFile => 
                existingFile.name === newFile.name && existingFile.size === newFile.size
            );
            
            if (!exists) {
                this.allFiles.push(newFile);
            }
        });
        
        // Update the file input with all files
        const dt = new DataTransfer();
        this.allFiles.forEach(file => dt.items.add(file));
        this.fileInput!.files = dt.files;
        
        // Display all files
        this.displaySelectedFiles();
    }

    private displaySelectedFiles(): void {
        if (!this.fileInput || !this.uploadList) return;

        // Clear previous list
        this.uploadList.innerHTML = '';

        // Display all selected files
        if (this.fileInput.files) {
            Array.from(this.fileInput.files).forEach((file, index) => {
                this.createFileElement(file, index);
            });
        }
    }

    private createFileElement(file: File, index: number): void {
        if (!this.uploadList) return;

        const fileElement = document.createElement('div');
        fileElement.className = 'cart-download-item';

        const fileName = this.getFileNameWithoutExtension(file.name);
        const fileExtension = this.getFileExtension(file.name);

        fileElement.innerHTML = `
            <div class="cart-download-item__text">
                <span class="cart-download-item__name">${fileName}</span>
                <span class="cart-dowmload-item__file-type">${fileExtension}</span>
            </div>
            <a href="javascript:" class="cart-download-item__remover" data-file-index="${index}">
                <img src="${(window as any).basePath || ''}/img/icon/category-tag-closer.svg" alt="ICON: Download item closer">
            </a>
        `;

        // Event listener for removing files
        const remover = fileElement.querySelector('.cart-download-item__remover') as HTMLElement;
        if (remover) {
            remover.addEventListener('click', () => {
                this.removeFile(index);
            });
        }

        this.uploadList.appendChild(fileElement);
    }

    private removeFile(indexToRemove: number): void {
        if (!this.fileInput) return;

        // Remove file from our internal array
        this.allFiles.splice(indexToRemove, 1);
        
        // Update the file input with remaining files
        const dt = new DataTransfer();
        this.allFiles.forEach(file => dt.items.add(file));
        this.fileInput.files = dt.files;
        
        // Refresh the display
        this.displaySelectedFiles();
    }

    private getFileNameWithoutExtension(filename: string): string {
        return filename.substring(0, filename.lastIndexOf('.')) || filename;
    }

    private getFileExtension(filename: string): string {
        const extension = filename.substring(filename.lastIndexOf('.'));
        return extension || '';
    }

    // Static method for initialization
    public static init(): void {
        new FileUploadManagerCart();
    }
}